<script>
// app/javascript/dashboard/components/layout/sidebarComponents/SecondaryNavItem.vue
import { ref, onMounted, computed } from 'vue';
import { mapGetters, useStore } from 'vuex';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useConfig } from 'dashboard/composables/useConfig';
import {
  getInboxClassByType,
  getInboxWarningIconClass,
} from 'dashboard/helper/inbox';

import SecondaryChildNavItem from './SecondaryChildNavItem.vue';
import {
  isOnMentionsView,
  isOnUnattendedView,
} from '../../../store/modules/conversations/helpers/actionHelpers';
import Policy from '../../policy.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import InboxModal from 'dashboard/components/InboxModal.vue';
import EditInboxModal from 'dashboard/components/EditInboxModal.vue';
import ReconnectModal from 'dashboard/components/ReconnectInboxModal.vue';
import SuccessModal from 'dashboard/components/SuccessModal.vue';

export default {
  components: { 
    SecondaryChildNavItem, 
    Policy, 
    NextButton, 
    InboxModal,
    EditInboxModal,
    ReconnectModal,
    SuccessModal
  },
  props: {
    menuItem: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['addLabel', 'open'],
  setup() {
    const { isAdmin } = useAdmin();
    const { isEnterprise } = useConfig();
    
    const showInboxModal = ref(false);
    const showEditInboxModal = ref(false);
    const showReconnectModal = ref(false);
    const showSuccessModal = ref(false);
    const selectedInboxName = ref(null);
    const selectedInboxId = ref(null);
    const selectedInboxMetaData = ref(null);
    
    // Novos refs para armazenar mensagem e título de sucesso
    const successTitle = ref('Tudo certo!');
    const successMessage = ref('Conexão realizada com sucesso. Seu inbox está sendo sincronizado e estará pronto para uso em breve.');

    // Open the Inbox modal
    const openInboxModal = (inboxName, inboxId) => {
      selectedInboxName.value = inboxName;
      selectedInboxId.value = inboxId;
      showInboxModal.value = true;
    };

    // abrir o modal de reconectar
    const openReconnectModal = (inboxName, inboxId, metadata = null) => {
      console.log('Opening reconnect modal for:', inboxName, inboxId);
      selectedInboxName.value = inboxName;
      selectedInboxId.value = inboxId;
      
      // If metadata is directly provided by the child component, use it
      if (metadata && (metadata.integration_type || metadata.display_phone_number)) {
        selectedInboxMetaData.value = metadata;
      } 
      // Otherwise try to find it from store
      else {
        const store = useStore();
        const metadata = store.getters['inboxMetadata/getInboxMetadata'](inboxId);
        if (metadata) {
          // Store the necessary metadata
          selectedInboxMetaData.value = {
            integration_type: metadata.integration_type || '',
            phone_number: metadata.profile || ''
          };
        }
      }
      
      showReconnectModal.value = true;
    }

    // Open the Edit Inbox modal
    const openEditInboxModal = (inboxName, inboxId, metadata = null) => {
      selectedInboxName.value = inboxName;
      selectedInboxId.value = inboxId;

      // If metadata is directly provided by the child component, use it
      if (metadata && (metadata.integration_type || metadata.display_phone_number || metadata.phone_number || metadata.connectionStatus !== undefined)) {
        selectedInboxMetaData.value = metadata;
      }
      // Otherwise try to find it from store
      else {
        const store = useStore();
        const metadata = store.getters['inboxMetadata/getInboxMetadata'](inboxId);
        if (metadata) {
          // Store the necessary metadata including connection status
          selectedInboxMetaData.value = {
            integration_type: metadata.integration_type || '',
            display_phone_number: metadata.profile || '',
            connectionStatus: metadata.status !== undefined ? metadata.status : 0
          };
        }
      }

      showEditInboxModal.value = true;
    };

    // Recebe informações de sucesso e abre o modal
    const openSuccessModal = (data = {}) => {
      // Atualiza as informações de título e mensagem se fornecidas
      if (data.title) {
        successTitle.value = data.title;
      }
      if (data.message) {
        successMessage.value = data.message;
      }
      
      showInboxModal.value = false;
      showEditInboxModal.value = false;
      showReconnectModal.value = false;
      showSuccessModal.value = true;
    };

    // Close modals without triggering reload
    const closeModals = () => {
      showInboxModal.value = false;
      showEditInboxModal.value = false;
      showReconnectModal.value = false;
      showSuccessModal.value = false;
      
      // Redefine os valores padrão para as próximas aberturas
      successTitle.value = 'Tudo certo!';
      successMessage.value = 'Conexão realizada com sucesso. Seu inbox está sendo sincronizado e estará pronto para uso em breve.';
      
      // Don't reload metadata on modal close - this causes infinite loops
      // Metadata updates are handled by WebSocket events from actual changes
    };
    
    // Get store instance
    const store = useStore();
    
    // Computed properties for inbox metadata from store
    const inboxMetaData = computed(() => {
      const allMetadata = store.getters['inboxMetadata/getAllInboxesMetadata'];
      // Transform to match the expected format
      return {
        results: Object.entries(allMetadata || {}).map(([inboxId, metadata]) => ({
          inbox_id: parseInt(inboxId),
          integration_type: metadata.integration_type,
          integration_id: metadata.integration_id,
          profile: metadata.profile,
          connectionStatus: metadata.status,
          inbox_name: metadata.inbox_name,
          display_name: metadata.display_name,
          hasAI: metadata.hasAI || false,
        }))
      };
    });
    
    const isLoading = computed(() => store.getters['inboxMetadata/isLoading']);
    const errorMessage = ref('');

    // Handle inbox deletion specifically
    const handleInboxDeleted = () => {
      // The backend will broadcast the update via WebSocket
      // No need to manually reload here
      closeModals();
    };

    // Component mount - metadata already loaded in App.vue
    onMounted(() => {
      // Metadata is already loaded in App.vue initialization
      // If needed, we can trigger a refresh here
    });

    return {
      isAdmin,
      isEnterprise,
      showInboxModal,
      showEditInboxModal,
      showReconnectModal,
      showSuccessModal,
      selectedInboxName,
      selectedInboxId,
      selectedInboxMetaData,
      successTitle,
      successMessage,
      openInboxModal,
      openEditInboxModal,
      openReconnectModal,
      openSuccessModal,
      closeModals,
      handleInboxDeleted,
      inboxMetaData,
      isLoading,
      errorMessage
    };
  },
  computed: {
    ...mapGetters({
      activeInbox: 'getSelectedInbox',
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      globalConfig: 'globalConfig/get',
    }),
    isCountZero() {
      return this.menuItem.count === 0;
    },
    isActiveView() {
      return this.computedClass.includes('active-view');
    },
    hasSubMenu() {
      return !!this.menuItem.children;
    },
    isMenuItemVisible() {
      if (this.menuItem.globalConfigFlag) {
        return !!this.globalConfig[this.menuItem.globalConfigFlag];
      }

      let isFeatureEnabled = true;
      if (this.menuItem.featureFlag) {
        isFeatureEnabled = this.isFeatureEnabledonAccount(
          this.accountId,
          this.menuItem.featureFlag
        );
      }

      if (this.menuItem.isEnterpriseOnly) {
        if (!this.isEnterprise) return false;
        return isFeatureEnabled || this.globalConfig.displayManifest;
      }

      if (this.menuItem.featureFlag) {
        return this.isFeatureEnabledonAccount(
          this.accountId,
          this.menuItem.featureFlag
        );
      }

      return isFeatureEnabled;
    },
    isAllConversations() {
      return (
        this.$store.state.route.name === 'inbox_conversation' &&
        this.menuItem.toStateName === 'home'
      );
    },
    isMentions() {
      return (
        isOnMentionsView({ route: this.$route }) &&
        this.menuItem.toStateName === 'conversation_mentions'
      );
    },
    isUnattended() {
      return (
        isOnUnattendedView({ route: this.$route }) &&
        this.menuItem.toStateName === 'conversation_unattended'
      );
    },
    isTeamsSettings() {
      return (
        this.$store.state.route.name === 'settings_teams_edit' &&
        this.menuItem.toStateName === 'settings_teams_list'
      );
    },
    isInboxSettings() {
      return (
        this.$route.name === 'settings_inbox_show' &&
        this.menuItem.toStateName === 'settings_inbox_list'
      );
    },
    isIntegrationsSettings() {
      return (
        this.$store.state.route.name === 'settings_integrations_webhook' &&
        this.menuItem.toStateName === 'settings_integrations'
      );
    },
    isApplicationsSettings() {
      return (
        this.$store.state.route.name === 'settings_applications_integration' &&
        this.menuItem.toStateName === 'settings_applications'
      );
    },
    isContactsDefaultRoute() {
      return (
        this.menuItem.toStateName === 'contacts_dashboard_index' &&
        (this.$store.state.route.name === 'contacts_dashboard_index' ||
          this.$store.state.route.name === 'contacts_edit')
      );
    },
    isCurrentRoute() {
      return this.$store.state.route.name.includes(this.menuItem.toStateName);
    },
    computedClass() {
      if (this.activeInbox) return ' ';
      if (
        this.isAllConversations ||
        this.isMentions ||
        this.isUnattended ||
        this.isContactsDefaultRoute ||
        this.isCurrentRoute
      ) {
        return 'bg-woot-25 dark:bg-slate-800 text-woot-500 dark:text-woot-500 hover:text-woot-500 dark:hover:text-woot-500 active-view';
      }
      if (this.hasSubMenu) {
        if (
          this.isTeamsSettings ||
          this.isInboxSettings ||
          this.isIntegrationsSettings ||
          this.isApplicationsSettings
        ) {
          return 'bg-woot-25 dark:bg-slate-800 text-woot-500 dark:text-woot-500 hover:text-woot-500 dark:hover:text-woot-500 active-view';
        }
        return 'hover:text-slate-700 dark:hover:text-slate-100';
      }

      return 'hover:text-slate-700 dark:hover:text-slate-100';
    },
  },
  methods: {
    computedInboxClass(child) {
      const { type, phoneNumber } = child;
      if (!type) return '';
      return getInboxClassByType(type, phoneNumber);
    },
    computedInboxErrorClass(child) {
      const { type, reauthorizationRequired } = child;
      if (!type) return '';
      return getInboxWarningIconClass(
        type,
        reauthorizationRequired
      );
    },
    newLinkClick(e, navigate) {
      if (this.menuItem.newLinkRouteName) {
        navigate(e);
      } else if (this.menuItem.showModalForNewItem) {
        if (this.menuItem.modalName === 'AddLabel') {
          e.preventDefault();
          this.$emit('addLabel');
        }
      }
    },
    showItem(item) {
      return this.isAdmin && !!item.newLink;
    },
    onClickOpen() {
      this.$emit('open');
    },
    showChildCount(count) {
      return Number.isInteger(count);
    },
    // Get specific inbox metadata for a child component
    getInboxMetaForId(inboxId) {
      if (!this.inboxMetaData || !this.inboxMetaData.results) {
        return {
          connectionStatus: null,
          displayText: 'Clique para conectar',
          metadata: null,
          integration_type: '',
          phone_number: '',
          hasAI: false,
        };
      }
      
      // Find metadata for the specific inbox
      for (const result of this.inboxMetaData.results) {
        if (result.inbox_id === inboxId) {
          // Ensure connectionStatus is a valid number (0, 1, or 2), default to null
          const rawStatus = result.connectionStatus;
          const connectionStatus = (rawStatus === 0 || rawStatus === 1 || rawStatus === 2) ? rawStatus : null;
          
          // Default text when not connected
          let displayText = 'Clique para conectar';
          
          // Format and extract the phone number for display
          let phoneNumber = '';
          const hasAI = result.hasAI === true;

          // Handle phone number display consistently for all connection types
          if (connectionStatus === 2 && result.profile) {
            // For Instagram and Messenger, use the profile as-is
            if (result.integration_type === 'instagram' || result.integration_type === 'messenger') {
              displayText = result.profile;
            } else {
              // For WhatsApp and other integrations, format as phone number
              const formatNumber = number => {
                // Remove any non-digit characters first
                const digitsOnly = number.replace(/\D/g, '');
                // Try to format as a Brazilian phone number if it matches the pattern
                if (/^\d{10,13}$/.test(digitsOnly)) {
                  return digitsOnly
                    .replace(/^(\d{2})(\d{2})(\d{5})(\d{4})$/, '+$1 $2 $3-$4')
                    .replace(/^(\d{2})(\d{2})(\d{4})(\d{4})$/, '+$1 $2 $3-$4');
                }
                return digitsOnly; // Just return the number without formatting
              };

              phoneNumber = formatNumber(result.profile);
              displayText = phoneNumber;
            }
          }
          
          return {
            connectionStatus,
            displayText,
            metadata: result,
            integration_type: result.integration_type || '',
            phone_number: phoneNumber,
            hasAI,
          };
        }
      }
      
      return {
        connectionStatus: null,
        displayText: 'Clique para conectar',
        metadata: null,
        integration_type: '',
        phone_number: '',
        hasAI: false,
      };
    }
  },
};
</script>

<template>
  <li v-show="isMenuItemVisible" class="mt-1">
    <div v-if="hasSubMenu" class="flex justify-between">
      <span
        class="px-2 pt-1 my-2 text-sm font-semibold text-slate-700 dark:text-slate-200"
      >
        {{ $t(`SIDEBAR.${menuItem.label}`) }}
      </span>
      <div v-if="menuItem.showNewButton" class="flex items-center">
        <NextButton ghost xs slate icon="i-lucide-plus" @click="onClickOpen" />
      </div>
    </div>
    <router-link
      v-else
      class="flex items-center p-2 m-0 text-sm leading-4 rounded-lg text-slate-700 dark:text-slate-100 hover:bg-slate-25 dark:hover:bg-slate-800"
      :class="computedClass"
      :to="menuItem && menuItem.toState"
    >
      <fluent-icon
        :icon="menuItem.icon"
        class="min-w-[1rem] mr-1.5 rtl:mr-0 rtl:ml-1.5"
        size="14"
      />
      {{ $t(`SIDEBAR.${menuItem.label}`) }}
      <span
        v-if="showChildCount(menuItem.count)"
        class="px-1 py-0 mx-1 rounded-md text-xxs"
        :class="{
          'text-slate-300 dark:text-slate-600': isCountZero && !isActiveView,
          'text-slate-600 dark:text-slate-50': !isCountZero && !isActiveView,
          'bg-woot-75 dark:bg-woot-200 text-woot-600 dark:text-woot-600':
            isActiveView,
          'bg-slate-50 dark:bg-slate-700': !isActiveView,
        }"
      >
        {{ `${menuItem.count}` }}
      </span>
      <span
        v-if="menuItem.beta"
        data-view-component="true"
        label="Beta"
        class="inline-block px-1 mx-1 leading-4 text-green-500 border border-green-400 rounded-lg text-xxs"
      >
        {{ $t('SIDEBAR.BETA') }}
      </span>
    </router-link>
    <ul v-if="hasSubMenu" class="list-none reset-base">
      <SecondaryChildNavItem
        v-for="child in menuItem.children"
        :key="child.id"
        :to="child.toState"
        :label="child.label"
        :inbox-id="child.id"
        :label-color="child.color"
        :should-truncate="child.truncateLabel"
        :icon="computedInboxClass(child)"
        :warning-icon="computedInboxErrorClass(child)"
        :show-child-count="showChildCount(child.count)"
        :child-item-count="child.count"
        :inbox-section-btn="menuItem.inboxSectionBtn"
        :inbox-meta-data="getInboxMetaForId(child.id)"
        :is-loading="isLoading"
        :error-message="errorMessage"
        @inboxModal="openInboxModal"
        @editInboxModal="openEditInboxModal"
        @reconnectModal="openReconnectModal"
      />
      <Policy :permissions="['administrator']">
        <router-link
          v-if="menuItem.newLink"
          v-slot="{ href, navigate }"
          :to="menuItem.toState"
          custom
        >
          <li class="pl-1">
            <a :href="href">
              <NextButton
                ghost
                xs
                slate
                icon="i-lucide-plus"
                :label="$t(`SIDEBAR.${menuItem.newLinkTag}`)"
                :data-testid="menuItem.dataTestid"
                @click="e => newLinkClick(e, navigate)"
              />
            </a>
          </li>
        </router-link>
      </Policy>
    </ul>
    <InboxModal
      v-if="showInboxModal"
      :inboxName="selectedInboxName"
      :inboxId="selectedInboxId"
      :show="showInboxModal"
      @close="closeModals"
      @success="openSuccessModal"
    />
    <EditInboxModal
      v-if="showEditInboxModal"
      :inboxName="selectedInboxName"
      :inboxId="selectedInboxId"
      :integration_type="selectedInboxMetaData?.integration_type || ''"
      :display_phone_number="selectedInboxMetaData?.display_phone_number || selectedInboxMetaData?.phone_number || ''"
      :connection_status="selectedInboxMetaData?.connectionStatus || ''"
      :viewOnly="selectedInboxMetaData?.viewOnly || false"
      :show="showEditInboxModal"
      @close="closeModals"
      @deleted="handleInboxDeleted"
    />
    <ReconnectModal
      v-if="showReconnectModal"
      :inboxName="selectedInboxName"
      :inboxId="selectedInboxId"
      :integration_type="selectedInboxMetaData?.integration_type || ''"
      :show="showReconnectModal"
      @close="closeModals"
      @deleted="handleInboxDeleted"
      @success="openSuccessModal"
    />
    <SuccessModal
      v-if="showSuccessModal"
      :show="showSuccessModal"
      :customTitle="successTitle"
      :customMessage="successMessage"
      @close="closeModals"
    />
  </li>
</template>
